package com.downdogapp.client.widget

import com.downdogapp.*
import com.downdogapp.Cursor.POINTER
import com.downdogapp.JustifyContent.CENTER
import com.downdogapp.JustifyContent.SPACE_AROUND
import com.downdogapp.JustifyContent.SPACE_BETWEEN
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.client.*
import com.downdogapp.client.controllers.HistoryItemViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Logger
import kotlinx.dom.clear
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLSpanElement

class CalendarView(selectWeekStart: () -> Unit,
                   decrementMonth: () -> Unit,
                   incrementMonth: () -> Unit,
                   private val getActiveYearMonth: () -> YearMonth) : AbstractView() {

  private var monthYearLabel: HTMLSpanElement
  private var content: HTMLDivElement

  private val activeYearMonth get() = getActiveYearMonth()

  override val root = createDiv {
    bgColor = Color.grey(0.15)
    borderRadius = px(10)
    padding = px(10)

    horizontalFlexBox(verticalAlign = AlignItems.CENTER, horizontalJustify = SPACE_BETWEEN) {
      marginBottom = px(-6)

      horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
        monthYearLabel = label(fontSize = 16, fontWeight = FontWeight.SEMIBOLD) {
          marginLeft = HORIZONTAL_CONTENT_GUTTER_PX
        }
        imageButton(Images.GOAL_SETTINGS, clickFn = selectWeekStart) {
          marginLeft = SETTINGS_ICON_LEFT_MARGIN
          marginBottom = px(1)
        }
      }
      horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
        imageButton(Images.LEFT_ARROW_ICON, decrementMonth)
        imageButton(Images.RIGHT_ARROW_ICON, incrementMonth)
      }
    }
    content = div()
  }

  init {
    refreshView()
  }

  fun refreshView() {
    monthYearLabel.text = activeYearMonth.monthYearNameLong

    content.apply {
      clear()

      val startingDayOfTheWeek = YearMonthDay(activeYearMonth, 1).dayOfWeek(StatsUtil.firstDayOfWeek)

      week().apply {
        (1..7).map {
          val weekdayName = YearMonthDay(activeYearMonth, it - startingDayOfTheWeek).weekdayName
          cell {
            label(text = weekdayName, fontSize = CALENDAR_FONT_SIZE, fontWeight = FontWeight.MEDIUM, color = Color.GREY)
          }
        }
      }

      var currentWeek: HTMLDivElement = week()

      for (day in 0..<startingDayOfTheWeek) {
        currentWeek.cell()
      }

      val daysToHistoryItems = StatsUtil.items.groupBy {
        YearMonthDay.fromTimestampSeconds(it.timestamp.seconds)
      }

      val numDaysInMonth = activeYearMonth.numDaysInMonth
      for (day in 1..numDaysInMonth) {
        val date = YearMonthDay(activeYearMonth, day)

        currentWeek.cell {
          val historyItems = daysToHistoryItems[date]
          if (!historyItems.isNullOrEmpty()) {
            circle(color = Color.DOWN_DOG_BLUE) {
              Logger.logEvent("calendar_day_clicked", mapOf("date" to date.toString()))
              App.pushViewController(HistoryItemViewController(historyItems[0]))
            }

            label(
              text = "$day",
              fontSize = CALENDAR_FONT_SIZE,
              fontWeight = if (date == YearMonthDay.today) FontWeight.SEMIBOLD else FontWeight.MEDIUM
            ) {
              zIndex = ZIndex.zVal(1)
            }

            if (historyItems.size > 1) {
              horizontalFlexBox(horizontalJustify = CENTER) {
                zIndex = ZIndex.zVal(1)
                width = percent(100)
                cursor = POINTER

                for (i in 0..<historyItems.size.coerceAtMost(3)) {
                  div {
                    bgColor = Color.WHITE
                    width = px(3)
                    height = px(3)
                    borderRadius = px(3)
                    marginLeft = px(1.5)
                    marginRight = px(1.5)
                  }
                }
              }
            } else if (date == YearMonthDay.today) {
              div { // underline
                zIndex = ZIndex.zVal(1)
                bgColor = Color.WHITE
                width = px(16)
                height = px(3)
              }
            }
          } else {
            label(
              "$day",
              CALENDAR_FONT_SIZE,
              if (date == YearMonthDay.today) FontWeight.SEMIBOLD else FontWeight.MEDIUM
            )
            if (date == YearMonthDay.today) {
              div { // underline
                bgColor = Color.WHITE
                width = px(16)
                height = px(3)
              }
            }
          }
        }

        if (date.dayOfWeek(StatsUtil.firstDayOfWeek) == 6 && day < numDaysInMonth) {
          currentWeek = week()
        }
      }

      for (day in YearMonthDay(activeYearMonth, numDaysInMonth).dayOfWeek(StatsUtil.firstDayOfWeek)..<6) {
        currentWeek.cell()
      }
    }
  }

  private fun HTMLElement.week() =
    horizontalFlexBox(horizontalJustify = SPACE_AROUND) {
      width = percent(100)
      height = CELL_HEIGHT
    }

  private fun HTMLElement.cell(block: HTMLDivElement.() -> Unit = {}) =
    verticalFlexBox(horizontalAlign = AlignItems.CENTER, verticalJustify = CENTER) {
      width = CELL_WIDTH
      block()
    }

  private fun HTMLElement.circle(color: Color,
                                 clickFn: (() -> Unit)? = null) = div {
    bgColor = color
    width = CELL_WIDTH
    height = CELL_WIDTH
    borderRadius = px(7)
    position = ABSOLUTE

    clickFn?.let {
      onClick {
        it()
      }
    }
  }

  companion object {

    private val SETTINGS_ICON_LEFT_MARGIN = px(8)
    private val CELL_WIDTH = px(40)
    private val CELL_HEIGHT = px(50)
    private val CALENDAR_FONT_SIZE = 16
  }
}

fun HTMLElement.calendar(selectWeekStart: () -> Unit,
                         decrementMonth: () -> Unit,
                         incrementMonth: () -> Unit,
                         getActiveYearMonth: () -> YearMonth,
                         init: HTMLDivElement.() -> Unit = {}): CalendarView {
  val view = CalendarView(selectWeekStart, decrementMonth, incrementMonth, getActiveYearMonth)
  append(view.root)
  view.root.apply(init)
  return view
}
